// Rename a machine and name a server (#6566) as pure data: the two request bodies, the
// toasts they say and who is offered each control; the card menu's Rename is `cardMenu`'s
// (`./machine-card`). The writes are `renameBox` (`PATCH /v1/runners/:id`,
// `./box-lifecycle`) and `setServerLabel` (`PUT /v1/runners/server-label`,
// `./server-label`); `./name-write` runs them.
//
// EMPTY AFTER TRIMMING IS `null`, NEVER `""`. On a machine `null` restores the generated
// "Adjective Noun" name (`generateDisplayName`, `@developerz/domain`, the same fill the
// fleet read gives an unnamed machine); on a server it clears the label. The route refuses
// `""` with a 400, so both builders trim and map blank to `null` here, once.
//
// A SERVER IS NAMED OFF THE UNFILTERED READ PLUS A GROUP KEY, never off a group's shown
// rows: a filter, a search or a page shows a subset, and the label belongs to every
// machine the read places on that server. A machine from ANOTHER org (`via_org`, owner
// ruling G23) is left out: #6556's account fence refuses the whole write over one foreign
// id. So on a server shared with another org's machines only this org's are labelled, and
// those others stay under "Unnamed server, <address>".

import { generateDisplayName } from '@developerz/domain';
import { UNPLACED_GROUP } from './fleet-machines';
import type { UtilBox } from './fleetUtilization';

/** `PATCH /v1/runners/:id`'s body: the trimmed name, or `null` to restore the generated one. */
export type RenameBody = { readonly display_name: string | null };

/** `PUT /v1/runners/server-label`'s body: every own-org machine on the server, one label. */
export type ServerLabelBody = {
  readonly runner_ids: readonly string[];
  readonly server_label: string | null;
};

/** A toast as a translation key and its variables; the caller translates it. */
export type ToastInput = {
  readonly key: string;
  readonly vars?: Readonly<Record<string, string>>;
};

const blankToNull = (text: string): string | null => {
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
};

export function renameBody(text: string): RenameBody {
  return { display_name: blankToNull(text) };
}

/** The name an empty field goes back to: the server's own fill for an unnamed machine. */
export function generatedName(box: Pick<UtilBox, 'id'>): string {
  return generateDisplayName(box.id);
}

/** "Renamed to <name>." or, for `null`, "Name reset to <generated>." */
export function renameToast(body: RenameBody, generated: string): ToastInput {
  return body.display_name === null
    ? { key: 'fleet.panel.name_reset', vars: { name: generated } }
    : { key: 'fleet.panel.renamed', vars: { name: body.display_name } };
}

/**
 * This org's own machines the fleet read places in the server group `groupKey`. The
 * unplaced group (`machine:`) is no server, so it names none.
 */
export function serverMachineIds(all: readonly UtilBox[], groupKey: string): readonly string[] {
  if (groupKey.startsWith(UNPLACED_GROUP)) return [];
  return all.filter((b) => b.via_org === null && b.server.key === groupKey).map((b) => b.id);
}

/** The label write for a server group, or null when the group holds none of this org's machines. */
export function serverLabelBody(
  all: readonly UtilBox[],
  groupKey: string,
  text: string,
): ServerLabelBody | null {
  const ids = serverMachineIds(all, groupKey);
  return ids.length === 0 ? null : { runner_ids: ids, server_label: blankToNull(text) };
}

/** "Server named <label>." or, for `null`, "Server name cleared." */
export function serverLabelToast(body: ServerLabelBody): ToastInput {
  return body.server_label === null
    ? { key: 'fleet.machines.server_name_cleared' }
    : { key: 'fleet.machines.server_named', vars: { name: body.server_label } };
}

/**
 * Rename is an admin's (owner ruling G21, `canAdminWrite` on a settled role), and only on
 * this org's own machine (G23).
 */
export function canRenameMachine(box: UtilBox, canWrite: boolean): boolean {
  return canWrite && box.via_org === null;
}

/**
 * A group header's "Name this server": an admin's, on a group holding at least one of this
 * org's machines (a group of only another org's machines gets none).
 */
export function canNameServer(
  all: readonly UtilBox[],
  groupKey: string,
  canWrite: boolean,
): boolean {
  return canWrite && serverMachineIds(all, groupKey).length > 0;
}

/** The details panel's Server edit: {@link canNameServer} on an own-org machine's server. */
export function canNameServerOf(box: UtilBox, all: readonly UtilBox[], canWrite: boolean): boolean {
  return box.via_org === null && canNameServer(all, box.server.key, canWrite);
}
